/**
 * authService — centralizes Supabase auth API for UI files.
 *
 * Public surface:
 *   - Named function exports (preferred for new code)
 *   - `authService` object (backwards-compatible facade)
 *
 * UI must NEVER import `@/integrations/supabase/client` directly to call
 * `auth.*`. Instead, import from the `@/services/auth` barrel.
 */
import { supabase } from "@/integrations/supabase/client";
import type {
  AuthChangeEvent,
  Session,
  Subscription,
} from "@supabase/supabase-js";

export type AuthSubscription = { subscription: Subscription };

/** Sign up a new user with email + password and a post-confirm redirect. */
export function signUpWithEmailPassword(
  email: string,
  password: string,
  redirectTo: string,
  data?: Record<string, unknown>,
) {
  return supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: redirectTo, data },
  });
}

/** Sign in an existing user with email + password. */
export function signInWithPassword(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

/** Send a password-reset email with the given redirect target. */
export function sendPasswordResetEmail(email: string, redirectTo: string) {
  return supabase.auth.resetPasswordForEmail(email, { redirectTo });
}

/** Trigger a passwordless / magic-link sign-in via email OTP. */
export function signInWithEmailOtp(email: string, emailRedirectTo: string) {
  return supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo },
  });
}

/**
 * Resend the signup confirmation email for a user that has registered but
 * not yet confirmed their email address. Uses Supabase's dedicated `resend`
 * endpoint (type: 'signup'), which is the correct call for unverified
 * accounts — `signInWithOtp` would attempt a magic-link login instead and
 * may fail or send the wrong template.
 */
export function resendSignupConfirmation(email: string, emailRedirectTo: string) {
  return supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo },
  });
}

/**
 * Coarse signal returned by the `check-email-verification-status` edge
 * function. Used by the UI BEFORE calling resend so we can give an honest
 * answer when an account is already verified (Supabase silently 200s on
 * resend in that case, which makes the UI lie to the user).
 *
 * - 'confirmed'   : account exists and is verified — resend is pointless
 * - 'unconfirmed' : account exists but not verified — resend is the right call
 * - 'unknown'     : we could not determine state — caller should fall back
 *                   to the default Supabase resend behavior
 */
export type EmailVerificationStatus = 'confirmed' | 'unconfirmed' | 'unknown';

/**
 * Lightweight pre-flight check used by the resend-confirmation flow.
 * Always resolves (never throws) — on any error it returns 'unknown' so the
 * caller can fall back to the existing resend path without UX disruption.
 */
export async function checkEmailVerificationStatus(
  email: string,
): Promise<EmailVerificationStatus> {
  try {
    const { data, error } = await supabase.functions.invoke<{
      status?: EmailVerificationStatus;
    }>('check-email-verification-status', {
      body: { email },
    });
    if (error) return 'unknown';
    const status = data?.status;
    if (status === 'confirmed' || status === 'unconfirmed') return status;
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Authoritative server-side verification snapshot for the currently
 * authenticated user (or, with admin role, for an arbitrary `userId`).
 *
 * Backed by the `verification-status` edge function, which reads
 * `auth.users.email_confirmed_at` directly via service-role and compares
 * it to `public.profiles.verification_status`. Use this in flows where
 * the profile column may be stale (e.g. right after the user clicked
 * the email confirmation link) and you need to decide based on the
 * canonical Auth state rather than a cached profile row.
 *
 * Resolves to `null` on any error so callers can fall back gracefully.
 */
export type ServerVerificationStatus = {
  user_id: string;
  email: string | null;
  email_confirmed_at: string | null;
  is_confirmed: boolean;
  derived_verification_status: 'email_verified' | 'unverified';
  profile_verification_status: string | null;
  in_sync: boolean;
};

export async function getServerVerificationStatus(
  userId?: string,
): Promise<ServerVerificationStatus | null> {
  try {
    const { data, error } = await supabase.functions.invoke<ServerVerificationStatus>(
      'verification-status',
      { body: userId ? { user_id: userId } : {} },
    );
    if (error || !data || typeof data.is_confirmed !== 'boolean') return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Verify a one-time email-link token (signup / magiclink / recovery / email_change).
 * Wrapped here so UI files don't need a direct supabase client import.
 */
export function verifyEmailLinkOtp(
  tokenHash: string,
  type: 'signup' | 'magiclink' | 'recovery' | 'email_change' | 'invite',
) {
  return supabase.auth.verifyOtp({ token_hash: tokenHash, type });
}

/**
 * Server-side healing of `profiles.verification_status` from `auth.users`.
 * Thin wrapper around the `sync_profile_verification_for_user` RPC so UI
 * files (BetaGate, admin user table) don't need a direct supabase import.
 */
export async function syncProfileVerificationForUser(userId: string) {
  const { error } = await supabase.rpc('sync_profile_verification_for_user', {
    _user_id: userId,
  });
  if (error) throw error;
}

/**
 * Classify an error from `supabase.auth.resend` to decide whether a retry
 * is safe. We must NOT retry on:
 *   - Rate limits (would burn the user's cooldown faster).
 *   - Validation errors (invalid email, already confirmed, etc.).
 *   - Auth-level 4xx (bad request, unauthorized).
 * We DO retry on transient backend conditions:
 *   - Network errors (fetch failed / no status).
 *   - 5xx server errors (worker boot failure, gateway error, queue hiccup).
 *   - 408 / 425 / 429-without-rate-msg / 503 / 504.
 */
function isTransientResendError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as { status?: number; message?: string; name?: string };
  const status = typeof anyErr.status === 'number' ? anyErr.status : undefined;
  const msg = (anyErr.message ?? '').toLowerCase();

  // Rate limit & explicit user-facing cooldown — never retry, surface immediately.
  if (status === 429) return false;
  if (msg.includes('rate') || msg.includes('only request') || msg.includes('seconds')) return false;

  // Network / fetch failures usually have no status.
  if (status === undefined) {
    if (anyErr.name === 'AbortError') return false;
    if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed')) {
      return true;
    }
    // Unknown shape with no status — treat as transient once.
    return true;
  }

  // 5xx and a few transient 4xx are worth retrying.
  if (status >= 500 && status <= 599) return true;
  if (status === 408 || status === 425) return true;

  return false;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Resend signup confirmation with exponential backoff so that a single
 * worker boot failure or transient 5xx does not cause the user-visible
 * "Re-send" button to fail. Non-transient errors (rate limits, validation)
 * are surfaced immediately without burning retry budget.
 *
 * Returns the same shape as `supabase.auth.resend` so callers don't change.
 */
export async function resendSignupConfirmationWithRetry(
  email: string,
  emailRedirectTo: string,
  opts: { maxAttempts?: number; baseDelayMs?: number } = {},
): Promise<Awaited<ReturnType<typeof resendSignupConfirmation>>> {
  const maxAttempts = Math.max(1, opts.maxAttempts ?? 3);
  const baseDelayMs = Math.max(50, opts.baseDelayMs ?? 500);

  let lastResult: Awaited<ReturnType<typeof resendSignupConfirmation>> | null = null;
  let lastThrown: unknown = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await resendSignupConfirmation(email, emailRedirectTo);
      lastResult = result;
      const err = result?.error ?? null;
      if (!err) return result;
      if (!isTransientResendError(err) || attempt === maxAttempts) return result;
    } catch (thrown) {
      lastThrown = thrown;
      if (!isTransientResendError(thrown) || attempt === maxAttempts) {
        throw thrown;
      }
    }

    // Exponential backoff with mild jitter: 500ms, 1500ms, 4500ms…
    const delay = baseDelayMs * Math.pow(3, attempt - 1);
    const jitter = Math.floor(Math.random() * 150);
    await sleep(delay + jitter);
  }

  if (lastResult) return lastResult;
  throw lastThrown ?? new Error('resend_failed');
}

/** Update the current user's password. */
export function updateUserPassword(password: string) {
  return supabase.auth.updateUser({ password });
}

/** Update the current user's email address (triggers re-confirmation). */
export function updateUserEmail(email: string) {
  return supabase.auth.updateUser({ email });
}

/** Sign the current user out. */
export function signOut() {
  return supabase.auth.signOut();
}

/** Read the current persisted session (e.g. on app boot). */
export async function getCurrentSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}

/**
 * Subscribe to auth state changes. The callback receives both the event
 * and the current session, so consumers can mirror auth state without
 * touching the supabase client directly.
 * Caller is responsible for unsubscribing.
 */
export function subscribeToAuthChanges(
  cb: (event: AuthChangeEvent, session: Session | null) => void,
): AuthSubscription {
  const { data } = supabase.auth.onAuthStateChange((event, session) =>
    cb(event, session),
  );
  return { subscription: data.subscription };
}

/**
 * Backwards-compatible facade. Prefer the named exports above for new code.
 * @deprecated Import the named functions from `@/services/auth` instead.
 */
export const authService = {
  signUpEmailPassword: signUpWithEmailPassword,
  signInWithPassword,
  resetPasswordForEmail: sendPasswordResetEmail,
  signInWithOtp: signInWithEmailOtp,
  updatePassword: updateUserPassword,
  updateEmail: updateUserEmail,
  onAuthStateChange: subscribeToAuthChanges,
  signOut,
  getCurrentSession,
};